import { Button } from "@package/subframe/components/Button";

interface Props {
  definitionName: string;
  mode: "SIMPLE" | "CHECKLIST" | "EXTERNAL_SERVICE";
  failureCount: number;
  /**
   * True when the task requires a certificate but none has been uploaded.
   * Submission will park the task in AWAITING_EVIDENCE rather than completing
   * it — the sheet renders consequence-disclosure copy so the practitioner
   * isn't surprised by the state they end up in.
   */
  willAwaitEvidence?: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}

/**
 * The audit ceremony — UX designer's recommendation. Replaces a plain submit
 * button with a deliberate confirmation moment. For checklist completions with
 * failures, communicates the consequences (follow-up actions, manager email)
 * at the point the user can still go back.
 *
 * Implemented as a fixed bottom sheet (mobile-first) overlaying the form;
 * dismissible without losing work.
 */
export function SubmitBottomSheet({
  definitionName,
  mode,
  failureCount,
  willAwaitEvidence = false,
  onCancel,
  onConfirm,
}: Props) {
  const hasFailures = mode === "CHECKLIST" && failureCount > 0;
  const failureNoun = failureCount === 1 ? "item" : "items";

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="w-full max-w-lg rounded-t-2xl bg-white p-6 shadow-2xl sm:rounded-2xl animate-slide-up">
        <div className="flex flex-col items-start gap-3">
          <span className="text-section-header font-section-header text-primary-navy-800">
            Submit completion
          </span>
          <span className="text-body font-body text-neutral-900">
            You&apos;re about to submit{" "}
            <span className="font-body-bold">{definitionName}</span>.
          </span>

          {hasFailures && (
            <div className="flex w-full flex-col gap-2 rounded-md border-l-4 border-amber-500 bg-amber-50 px-3 py-2">
              <span className="text-body-bold font-body-bold text-amber-900">
                {failureCount} {failureNoun} marked as failed
              </span>
              <span className="text-caption font-caption text-amber-900">
                {failureCount} follow-up action{failureCount === 1 ? "" : "s"}{" "}
                will be created and assigned to the task&apos;s responsible
                role. Your nursery managers will be emailed a summary.
              </span>
            </div>
          )}

          {willAwaitEvidence && (
            <span className="text-body font-body text-neutral-900">
              You haven&apos;t attached a certificate yet. This task will be
              marked as submitted and awaiting your certificate — you can add it
              from the task page when it arrives.
            </span>
          )}

          <span className="text-caption font-caption text-subtext-color">
            Once submitted, this entry becomes part of your nursery&apos;s
            compliance audit trail.
          </span>
        </div>

        <div className="flex w-full justify-end gap-3 pt-6">
          <Button variant="neutral-secondary" onClick={onCancel}>
            Go back
          </Button>
          <Button onClick={onConfirm}>Confirm and submit</Button>
        </div>
      </div>
    </div>
  );
}
